import { useState, useEffect, useRef, useMemo } from "react";
import useGetData from "../Custom-Hooks/useGetData";
import { DOCSTORE_TABS, DocstoreType } from "../../utils/docstoreTabs";
import axios from "axios";
import GlobalLoader from "../shared/GlobalLoader";
import { useLoading } from "../../context/LoadingContext";
import MasterList from "./masterList";
import FolderList from "../FileExplorer/FolderList";
import Breadcrumb from "../FileExplorer/Breadcrumb";
import SuccessPopup from "../shared/SuccessPopup";
import { toast } from "react-toastify";
import { formatBytes } from "../utils/generalUtilities";
import { allowedExtensions } from "../../constants/uploadContants";
import { Dropdown } from "react-bootstrap";
import { FaList, FaThLarge, FaCheck } from "react-icons/fa";

const DocStore = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const accessToken = sessionStorage.getItem("accessToken");
  const headers = accessToken ? { Authorization: accessToken } : {};
  const selectedLocationId = localStorage.getItem("selectedLocationId") || 0;
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");

  const [activeTab, setActiveTab] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);
  const [docStorePermissions, setDocStorePermissions] = useState([]);
  const [currentCoreOpenTab, setCurrentCoreOpenTab] = useState(null);
  const [explorerData, setExplorerData] = useState([]);
  const [isInsideCore, setIsInsideCore] = useState(false);
  const { isLoading, setLoading } = useLoading();

  const [mebFiles, setMebFiles] = useState([]);
  const [ladFiles, setLadFiles] = useState([]);
  const [edFiles, setEdFiles] = useState([]);
  const [ladId, setLadid] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [folderName, setFolderName] = useState("");
  const [folderType, setFolderType] = useState("");
  const [folderList, setFolderList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const fileInputRef = useRef(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });
  const [storageSize, setStorageSize] = useState(0); // in bytes
  const [itemCount, setItemCount] = useState(0);
  const [viewMode, setViewMode] = useState("table");
  const [modalState, setModalState] = useState({ isOpen: false });
  const [selectedUser, setSelectedUser] = useState(null);
  const isAdmin = userDetails?.id === 1;

  const { data: allUserData } = useGetData(
    `${baseUrl}/user/${userDetails?.id}`
  );
  const { data: allMebLadData } = useGetData(
    `${baseUrl}/explorer/location/${selectedLocationId}/meb`
  );

  const systemId = localStorage.getItem("selectedSystemId");

  useEffect(() => {
    if (allMebLadData?.data?.length) {
      const meb = allMebLadData.data.map((item) => ({
        id: item.id,
        title: item.title.trim().endsWith(".")
          ? `${item.title}docx`
          : `${item.title}.docx`,
        key: item.generatedDoc?.key,
        created_at: item.generatedDoc?.created_at,
        size: item.generatedDoc?.size,
      }));
      const lad = allMebLadData.data.flatMap((item) =>
        item.ladDrawings.map((lad) => ({
          id: lad.id,
          name: lad.name.trim().endsWith(".")
          ? `${lad.name}png` 
          :`${lad.name}.png`,
          key: lad.name,
          updated_at: lad.updated_at,
          size: item.generatedDoc?.size,
        }))
      );
      const result = allMebLadData.data.flatMap((item) =>
        item.ladDrawings.flatMap((lad) =>
          lad.drawings.map((drawing) => ({
            ...drawing,
            ladId: lad.ladId,
            parentId: item.id,
            parentTitle: item.title,
          }))
        )
      );
      setEdFiles(result);
      setMebFiles(meb);
      setLadFiles(lad);
    }
    if (allMebLadData?.data[0]?.ladDrawings[0]?.ladId) {
      setLadid(allMebLadData?.data[0]?.ladDrawings[0]?.ladId);
    }
  }, [allMebLadData]);

  useEffect(() => {
    if (allUserData?.data?.featurePermissions) {
      setUserPermissions(allUserData.data.featurePermissions);
      setDocStorePermissions(allUserData.data.docStorePermissions);
    }
  }, [allUserData]);

  const availableTabs = Object.entries(DOCSTORE_TABS).filter(([_, id]) =>
    userPermissions.includes(Number(id))
  );

  useEffect(() => {
    if (availableTabs.length > 0 && !activeTab) {
      setActiveTab(availableTabs[0]?.[0]);
    }
  }, [availableTabs]);

  useEffect(() => {
    if (activeTab !== "Master List") {
      // Skip loading for 'Master List' tab
      if (activeTab) {
        loadFolderContent(currentFolderId);
      }
    }
  }, [activeTab, currentCoreOpenTab, currentFolderId]);

  const handleViewChange = (mode) => {
    setViewMode(mode);
  };

  const fetchStorageInfo = async () => {
    try {
      const { data } = await axios.get(
        `${baseUrl}/storage/storage-size-count/${systemId}`,
        {
          headers,
        }
      );
      setStorageSize(data.totalSize || 0);
      setItemCount(data.totalFiles || 0);
    } catch (err) {
      console.error("Failed to fetch storage info", err);
    }
  };

  useEffect(() => {
    if (systemId) {
      fetchStorageInfo();
    }
  }, [systemId]);

  const loadFolderContent = async (parentId = null) => {
    try {
      const isSystemLevel = DocstoreType[activeTab] !== "USER_FILES";
      const payload = {
        parentId: parentId,
        isSystemLevel,
        docstoreType: DocstoreType[activeTab],
      };

      if (isSystemLevel) {
        if (!systemId) {
          console.error("System ID not found in localStorage");
          return;
        }
        payload.systemId = parseInt(systemId);
        payload.user = userDetails?.id;
      } else {
        payload.user = userDetails?.id;
        payload.systemId = parseInt(systemId);
      }

      const { data } = await axios.post(`${baseUrl}/storage/list`, payload, {
        headers,
      });

      setFolderList(data.folders);
      setFileList(data.files);
      setCurrentFolderId(parentId);
    } catch (err) {
      console.error("Failed to load folder content:", err);
    }
  };

  const onTabChange = (tabName) => {
    setActiveTab(tabName);
    setCurrentCoreOpenTab(null);
    setIsInsideCore(false);
    setExplorerData([]);
    setCurrentFolderId(null);
    setSearchQuery("");
  };

  const createFolder = async () => {
    const finalFolderType =
      folderType?.trim() ||
      (activeTab === "User Files" ? "User folder" : "Team folder");

    if (!folderName?.trim()) return;

    const isSystemLevel = DocstoreType[activeTab] !== "USER_FILES";

    if (isSystemLevel) {
      if (!systemId) {
        setShowSuccessPopup({
          type: "warning",
          show: true,
          message:
            "System not selected. Please select a system before creating folders.",
        });
        return;
      }

      try {
        const response = await axios.post(
          `${baseUrl}/storage/folder`,
          {
            name: folderName.trim(),
            folderType: finalFolderType,
            user: userDetails?.id,
            parentId: currentFolderId,
            docstoreType: DocstoreType[activeTab],
            isSystemLevel,
            systemId: systemId,
          },
          { headers }
        );
        if (response.status == 200) {
          toast.success(`Folder created successfully`, {
            autoClose: 5000,
            closeButton: true,
            theme: "colored",
          });
        }

        setFolderName("");
        setFolderType("");
        setModalState({ isOpen: false });
        loadFolderContent(currentFolderId);
      } catch (error) {
        console.error("Error creating folder:", error);
        let errorMessage = "Failed to create folder.";

        if (error?.response?.data?.error) {
          errorMessage += ` ${error.response.data.error}`;
        } else if (error?.response?.data?.message) {
          errorMessage += ` ${error.response.data.message}`;
        } else if (typeof error === "string") {
          errorMessage += ` ${error}`;
        } else if (error?.message) {
          errorMessage += ` ${error.message}`;
        }
        toast.error(`${errorMessage}`, {
          autoClose: 5000,
          theme: "colored",
        });
      }
    } else {
      try {
        await axios.post(
          `${baseUrl}/storage/folder`,
          {
            name: folderName.trim(),
            user: userDetails?.id,
            parentId: currentFolderId,
            docstoreType: DocstoreType[activeTab],
            isSystemLevel: false,
            systemId: systemId,
          },
          { headers }
        );
        setFolderName("");
        setFolderType("");
        setModalState({ isOpen: false });
        loadFolderContent(currentFolderId);
      } catch (error) {
        console.error("Error creating folder:", error);
        setShowSuccessPopup({
          type: "warning",
          show: true,
          message: "Failed to create folder.",
        });
      }
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(`File format not allowed: ${file.name}`, {
          autoClose: 5000,
          theme: "colored",
        });
        continue;
      }

      const toastId = toast.loading(`Uploading ${file.name}...`, {
        closeButton: true,
        autoClose: false,
        theme: "colored",
      });

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("userId", userDetails?.id);
        formData.append("docstoreType", DocstoreType[activeTab]);
        formData.append("folderId", currentFolderId || "");
        formData.append("systemId", systemId);

        const isSystemLevel = DocstoreType[activeTab] !== "USER_FILES";
        formData.append("isSystemLevel", isSystemLevel.toString());

        if (isSystemLevel) {
          if (!systemId) {
            toast.update(toastId, {
              render: "System not selected",
              type: "error",
              isLoading: false,
              autoClose: 8000,
              closeButton: true,
            });
            return;
          }
        }

        await axios.post(`${baseUrl}/storage/upload`, formData, { headers });

        toast.update(toastId, {
          render: `${file.name} uploaded successfully`,
          type: "success",
          isLoading: false,
          autoClose: 8000,
          closeButton: true,
        });

        fetchStorageInfo();
      } catch (err) {
        console.error(`Upload failed for ${file.name}`, err);
        let errorMessage = `Failed to upload`;
        if (err?.response?.data?.details) {
          errorMessage += `: ${err.response.data.details}`;
        }
        errorMessage += ` - ${file.name}`;
        toast.update(toastId, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 8000,
          closeButton: true,
        });
      }
    }

    loadFolderContent(currentFolderId);
    e.target.value = ""; // reset file input
  };

  const navigateToFolder = (folder) => {
    loadFolderContent(folder.id);
  };

  const getTabLabel = (tabName) => `Core - ${tabName}`;

  const filteredFolders = useMemo(() => {
    const coreTab =
      currentCoreOpenTab !== activeTab && activeTab !== "User Files"
        ? [
            {
              id: "core-tab",
              name: getTabLabel(activeTab),
              isCoreTab: true,
            },
          ]
        : [];

    return [
      ...coreTab,
      ...folderList.filter((folder) =>
        folder.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    ];
  }, [folderList, searchQuery, currentCoreOpenTab, activeTab]);

  const filteredFiles = useMemo(() => {
    return fileList.filter((file) =>
      file.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [fileList, searchQuery]);

  return (
    <div className="container-fluid">
      {isLoading && <GlobalLoader />}
      <div
        className="row rounded mx-2 mb-4 theme-container"
        style={{
          backgroundSize: "280px auto",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div className="col-12 p-3">
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-4 small fw-bold mt-3">
              {availableTabs.map(([tabName, id]) => (
                <div
                  key={id}
                  className="position-relative"
                  onClick={() => onTabChange(tabName)}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`pb-3 ${
                      activeTab === tabName
                        ? "active-blue-tab"
                        : "text-secondary"
                    }`}
                    style={{
                      borderBottom:
                        activeTab === tabName
                          ? "2px solid #25a9e0"
                          : "2px solid transparent",
                    }}
                  >
                    {tabName === "User Files" ? "Work Space" : tabName}
                  </span>
                </div>
              ))}
            </div>
            <div>
              <h5 className="mb-1 fnt17">Doc Store</h5>
              <p className="mb-0 text-secondary small fw-500 fnt14">
                {formatBytes(storageSize)} | {itemCount} items
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="row rounded mx-2 mb-4 theme-container">
        <div className="table-responsive">
          {activeTab === "Master List" ? (
            <div className="py-2">
              <MasterList
                ladId={ladId}
                docStorePermissions={docStorePermissions}
              />
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3 px-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{
                    maxWidth: "250px",
                    border: "1px solid #d1d5db",
                    paddingLeft: "2rem",
                    backgroundImage:
                      'url("data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="gray" class="bi bi-search" viewBox="0 0 16 16"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.415l-3.85-3.85a1.007 1.007 0 0 0-.115-.098zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"/></svg>")',
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "8px center",
                    backgroundSize: "16px 16px",
                  }}
                  placeholder="Search Files & Folders"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                <div className="d-flex gap-2">
                  <div className="d-flex gap-2 py-2">
                    {/* === New Folder Button === */}
                    <button
                      className={`btn d-flex align-items-center border fnt14 gap-2 px-2 py-2 ${
                        (activeTab === "User Files" && isAdmin) ||
                        (activeTab !== "User Files" &&
                          !docStorePermissions.includes("CREATE_FOLDER"))
                          ? "btn-disabled"
                          : ""
                      }`}
                      style={{
                        backgroundColor:
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("CREATE_FOLDER"))
                            ? "#e9ecef"
                            : "#ebf8ff",
                        color:
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("CREATE_FOLDER"))
                            ? "#6c757d"
                            : "#1c88bf",
                        fontWeight: 400,
                        cursor:
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("CREATE_FOLDER"))
                            ? "not-allowed"
                            : "pointer",
                      }}
                      disabled={
                        (activeTab === "User Files" && isAdmin) ||
                        (activeTab !== "User Files" &&
                          !docStorePermissions.includes("CREATE_FOLDER"))
                      }
                      onClick={() => {
                        if (
                          !(activeTab === "User Files" && isAdmin) &&
                          (activeTab === "User Files" ||
                            docStorePermissions.includes("CREATE_FOLDER"))
                        ) {
                          setModalState({ isOpen: true });
                        }
                      }}
                      title={
                        (activeTab === "User Files" && isAdmin) ||
                        (activeTab !== "User Files" &&
                          !docStorePermissions.includes("CREATE_FOLDER"))
                          ? "No permission granted"
                          : "Create a new folder"
                      }
                    >
                      <i
                        className={`bi bi-folder-plus ${
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("CREATE_FOLDER"))
                            ? "text-muted"
                            : "text-primary"
                        }`}
                      ></i>
                      New Folder
                    </button>

                    {/* === Upload Files Button === */}
                    {(activeTab === "User Files" ||
                      (activeTab !== "User Files" &&
                        ((folderList?.length > 0 &&
                          folderList?.[0]?.parentId !== null) ||
                          (folderList?.length === 0 &&
                            currentFolderId !== null)))) && (
                      <button
                        className={`btn d-flex align-items-center gap-2 fnt14 px-3 py-2 ${
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("UPLOAD_FILE"))
                            ? "btn-disabled"
                            : ""
                        }`}
                        style={{
                          backgroundColor:
                            (activeTab === "User Files" && isAdmin) ||
                            (activeTab !== "User Files" &&
                              !docStorePermissions.includes("UPLOAD_FILE"))
                              ? "#e9ecef"
                              : "#1cace9",
                          color:
                            (activeTab === "User Files" && isAdmin) ||
                            (activeTab !== "User Files" &&
                              !docStorePermissions.includes("UPLOAD_FILE"))
                              ? "#6c757d"
                              : "white",
                          fontWeight: 400,
                          cursor:
                            (activeTab === "User Files" && isAdmin) ||
                            (activeTab !== "User Files" &&
                              !docStorePermissions.includes("UPLOAD_FILE"))
                              ? "not-allowed"
                              : "pointer",
                        }}
                        disabled={
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("UPLOAD_FILE"))
                        }
                        onClick={() => {
                          if (
                            !(activeTab === "User Files" && isAdmin) &&
                            (activeTab === "User Files" ||
                              docStorePermissions.includes("UPLOAD_FILE"))
                          ) {
                            fileInputRef.current?.click();
                          }
                        }}
                        title={
                          (activeTab === "User Files" && isAdmin) ||
                          (activeTab !== "User Files" &&
                            !docStorePermissions.includes("UPLOAD_FILE"))
                            ? "No permission granted"
                            : "Upload files"
                        }
                      >
                        <i
                          className={`bi bi-file-earmark-image ${
                            (activeTab === "User Files" && isAdmin) ||
                            (activeTab !== "User Files" &&
                              !docStorePermissions.includes("UPLOAD_FILE"))
                              ? "text-muted"
                              : "text-white"
                          }`}
                        ></i>
                        Upload Files
                      </button>
                    )}

                    {/* Hidden File Input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      multiple
                      onChange={handleFileUpload}
                    />
                  </div>

                  <div className="d-flex justify-content-end mt-2">
                    <Dropdown>
                      <Dropdown.Toggle
                        // variant="outline-info"
                        id="view-mode-dropdown"
                        className="d-flex align-items-center justify-content-center square-toggle"
                      >
                        {viewMode === "list" ? (
                          <FaThLarge className="toggle-icon" />
                        ) : (
                          <FaList className="toggle-icon" />
                        )}
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="theme-container border p-0 py-1">
                        <Dropdown.Header>
                          <div className="fnt12 fnt-600">View Mode</div>
                        </Dropdown.Header>

                        <Dropdown.Item
                          className="d-flex align-items-center justify-content-between"
                          onClick={() => handleViewChange("table")}
                        >
                          <div className="d-flex align-items-center">
                            {viewMode === "table" && (
                              <FaCheck className="me-1 fnt14 text-info" />
                            )}
                            <span
                              className={
                                viewMode === "table"
                                  ? "text-info fnt14 fw-600"
                                  : ""
                              }
                            >
                              List
                            </span>
                          </div>
                          <FaList className="text-secondary me-1" />
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="d-flex align-items-center justify-content-between"
                          onClick={() => handleViewChange("list")}
                        >
                          <div className="d-flex align-items-center">
                            {viewMode === "list" && (
                              <FaCheck className="me-1 fnt14 text-info" />
                            )}
                            <span
                              className={
                                viewMode === "list"
                                  ? "text-info fnt14 fw-600"
                                  : ""
                              }
                            >
                              Grid
                            </span>
                          </div>
                          <FaThLarge className="text-secondary me-1" />
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>

              <Breadcrumb
                currentFolderId={currentFolderId}
                onNavigate={(folderId) => loadFolderContent(folderId)}
                activeTab={activeTab}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
              />

              <div className="px-4">
                <FolderList
                  folders={filteredFolders}
                  onFolderClick={navigateToFolder}
                  activeTab={activeTab}
                  currentCoreOpenTab={currentCoreOpenTab}
                  setCurrentCoreOpenTab={setCurrentCoreOpenTab}
                  currentFolderId={currentFolderId}
                  loadFolderContent={loadFolderContent}
                  files={filteredFiles}
                  mebFiles={mebFiles}
                  ladFiles={ladFiles}
                  edFiles={edFiles}
                  docStorePermissions={docStorePermissions}
                  fetchStorageInfo={fetchStorageInfo}
                  viewMode={viewMode}
                  isAdmin={isAdmin}
                  selectedUser={selectedUser}
                  setSelectedUser={setSelectedUser}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {modalState.isOpen && (
        <div
          className="modal fade modal-save-as show show-modal modal--opacity"
          id="docstore-rename-folder-form"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <div className="modal-dialog" style={{ minWidth: "400px" }}>
            <div className="modal-content">
              {/* Modal Header */}
              <div className="modal-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {/* Folder icon box */}
                  <div
                    style={{
                      border: "1px solid #f28c48",
                      borderRadius: "8px",
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <i
                      className="bi bi-folder-fill"
                      style={{ fontSize: "1.5rem", color: "#1c88bf" }}
                    ></i>
                  </div>

                  {/* Title + Subtitle */}
                  <div>
                    <div className="fnt14 fw-600">Add New Folder</div>
                    <div className="fnt12" style={{ color: "#f28c48" }}>
                      All fields are mandatory *
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setFolderName("");
                    setFolderType("");
                    setModalState({ isOpen: false });
                  }}
                ></button>
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                <div className="form-floating mb-2">
                  <select
                    type="text"
                    className="form-select floatingInput"
                    placeholder=""
                    value={folderType}
                    onChange={(e) => setFolderType(e.target.value)}
                  >
                    <option
                      value="Team folder"
                      disabled={activeTab === "User Files"}
                    >
                      Team folder
                    </option>
                    <option
                      value="Share folder"
                      disabled={activeTab === "User Files"}
                    >
                      Share folder
                    </option>
                    <option
                      value="User folder"
                      disabled={activeTab !== "User Files"}
                    >
                      User folder
                    </option>
                  </select>
                  <label className="floatingInput-label" htmlFor="customer">
                    Folder Type
                  </label>
                </div>

                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control floatingInput"
                    placeholder=""
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                  />
                  <label className="floatingInput-label" htmlFor="customer">
                    Folder name
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setFolderType("");
                    setFolderName("");
                    setModalState({ isOpen: false });
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createFolder}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default DocStore;
